import React, { useMemo } from 'react';
import wellboat from '../../../../../assets/icon/wellboat.svg';
import { InfoMode, OPERATION_TYPE, OP_STATUS } from "../../../../../util/varibles/constants";
import { formatNumber } from '../../../../../util/varibles/global';
import { IDiseaseOfSite, IRealTreatmentUnit2, ITaskTreatment, ITreatment2, IUnit, TTaskTreatment } from "../../../../../util/varibles/interface";
import Tabs from "../../../../Basic/Tabs";
import Icon, { ICON_NAME } from "../../../../Icon";
import UnitDisease from "../../../../Operation/FishDisease/UnitDisease";
import LiceReduction from "../../../../Operation/LiceReduction";
import MortalityIncrease from "../../../../Operation/MortalityIncrease";
import TankDensities from "../../../../Operation/TankDensities";
import TaskView from '../../../../Operation/Task/TaskView';
import UnitLice from "../../../../Operation/UnitLice";
import UnitMetricsTooltip from '../../../../Tooltip/UnitMetricsTooltip';
import FishHealthInfo from "../../_component/FishHealthInfo";
import LineInfo from "../../_component/LineInfo";
import stylesContainer from "../../style.module.scss";
import styles from './style.module.scss';

type TUnitExtand = Partial<IUnit> & { real_sub_operation?: IRealTreatmentUnit2 }

interface IProps {
    data: ITreatment2
    infoMode: InfoMode
    isShortLabel?: boolean
}

const UnitInfo: React.FC<{
    data?: TUnitExtand,
    diseases?: IDiseaseOfSite[],
    tasks?: { [id: string]: ITaskTreatment[] }
    infoMode: InfoMode
    isShortLabel?: boolean
    operationStatus?: OP_STATUS
}> = ({ data, diseases = [], tasks = {}, infoMode, isShortLabel, operationStatus }) => {
    if (!data)
        return null;

    const {
        id = "",
        avg_weight = 0,
        fish_amount = 0,
        total_weight = 0,
        note = "",
        lice,
        fish_health,
        storages,
        round_number,
        real_sub_operation
    } = data;

    return <>
        <div className='grid h-[26px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[15px]'>
            <div className="[&>div]:!pl-[20px]">
                <FishHealthInfo value={fish_health} />
            </div>
            {infoMode === InfoMode.ACTUAL &&
                <LiceReduction value={real_sub_operation?.lice_reduction} />}
        </div>
        <div className='grid h-[26px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[15px]'>
            <LineInfo label="Unit diseases" className='h-[26px]' icon>
                <>
                    <Icon className="mt-[2px]" icon={ICON_NAME.DISEASE} />
                    <UnitDisease unit={data} />
                </>
            </LineInfo>
            {infoMode === InfoMode.ACTUAL &&
                <MortalityIncrease value={real_sub_operation?.mortality_increase} />}
        </div>
        <div className='grid h-[26px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[15px]'>
            <LineInfo label="Lice per fish" icon className="h-[26px] pl-1">
                <>
                    <Icon className="mt-[2px]" icon={ICON_NAME.LICE} />
                    <UnitLice lice={lice || (diseases[0] || {}).lice} />
                </>
            </LineInfo>
            <UnitMetricsTooltip />
        </div>
        <LineInfo label="Fish group ID" className='mb-1' icon>
            <>{data.fish_group || "-"}</>
        </LineInfo>
        <div className='mb-[8px]'>
            <TankDensities type={OPERATION_TYPE.TREATMENT} storages={storages} />
        </div>
        {note.length > 0 && <div className={[stylesContainer['container-box'], 'mb-10 pl-1'].join(' ')}>
            <div>Unit's note:</div>
            <div className='ml-[1px]'>{note}</div>
        </div>}
        <div className={styles.table}>
            <div data-lev="header">
                <div>Amount</div>
                <div>Avg. weight</div>
                <div>Total weight</div>
                <div>Treatment type</div>
                <div>Round</div>
                <div>Dead fish</div>
            </div>
            {infoMode === InfoMode.PLAN && <div data-lev="info">
                <div>{formatNumber(fish_amount)}</div>
                <div>{formatNumber(Math.round(avg_weight))} gr</div>
                <div>{formatNumber(Math.round(total_weight / 1000) / 1000)} t</div>
                <div><TaskView isShortLabel={isShortLabel} tasks={tasks[id] || []} isLabel={false} /></div>
                <div>{round_number}</div>
                <div>--</div>
            </div>}
            {(infoMode === InfoMode.ACTUAL && real_sub_operation) && <div data-lev="info">
                <div>{formatNumber(real_sub_operation.fish_amount)}</div>
                <div>{formatNumber(Math.round(real_sub_operation.avg_weight || 0))} gr</div>
                <div>{real_sub_operation.total_weight ? `${formatNumber(Math.round(real_sub_operation.total_weight / 1000) / 1000)}t` : "-"}</div>
                <div><TaskView isShortLabel={isShortLabel} tasks={(real_sub_operation.tasks || []) as TTaskTreatment[]} isLabel={false} /></div>
                <div>{real_sub_operation.round_number}</div>
                <div>{real_sub_operation.dead_fish ? real_sub_operation.dead_fish : '-'}</div>
            </div>}
            {(infoMode === InfoMode.ACTUAL && !real_sub_operation) && <div data-lev="info" className='min-h-[46px]'>
                <div className='col-span-6 text-center !text-[13px] font-[600] text-comment'>No data</div>
            </div>}
        </div>
    </>
}

const Tasks: React.FC<IProps> = ({ data, infoMode, isShortLabel }) => {
    const { sub_operations, tasks, diseases = [], real_sub_operations = [] } = data;
    const tasksByUnit = tasks.reduce((rs: { [id: string]: ITaskTreatment[] }, item) => {
        const { id = "", } = item.sub_operation || {};
        rs[id] = [...rs[id] || [], item];
        return rs;
    }, {});

    const tabs = useMemo(() => {
        if (infoMode === InfoMode.PLAN) {
            return sub_operations.map(item => ({
                name: item.unit_id,
                data: item
            }));
        }

        return real_sub_operations.map(item => {
            const plan = sub_operations.find(sub => sub.unit_id === item.unit_id);

            return {
                name: item.unit_id,
                data: { ...plan, unit_id: item.unit_id, real_sub_operation: item }
            };
        });
    }, [infoMode, sub_operations, real_sub_operations]);

    if (infoMode === InfoMode.ACTUAL && real_sub_operations.length === 0) {
        return <div className='flex h-[180px] w-full flex-col items-center justify-center'>
            <img src={wellboat} alt="" className='h-[35px] w-[80px]' />
            <span className='text-[15px] italic text-comment'>Waiting for vessel data</span>
        </div>;
    }

    if (tabs.length === 0) {
        return <div className={styles.table}>
            <div data-lev="header">
                <div>Amount</div>
                <div>Avg. weight</div>
                <div>Total weight</div>
                <div>Treatment type</div>
                <div>Round</div>
                <div>Dead fish</div>
            </div>
            <div data-lev="info" className='min-h-[46px]'>
                <div className='col-span-6 text-center !text-[13px] font-[600] text-comment'>No data</div>
            </div>
        </div>;
    }

    return <Tabs opts={tabs}>
        <UnitInfo tasks={tasksByUnit} diseases={diseases} infoMode={infoMode}
            isShortLabel={isShortLabel} operationStatus={data.status} />
    </Tabs>;
};

export default Tasks;
